import { useState, useEffect, useCallback } from 'react';
import axios from '../../axiosConfig';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { useCalendar } from '../../context/CalendarContext';
import { mergeCalendarEvents } from '../../utils/calendar/calendarHelpers';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

/**
 * Hook for managing calendar data fetching and state
 */
export const useCalendarData = ({ trainerId, schoolId, weekStart }) => {
    const { getWeekStart, getWeekEnd } = useCalendar();

    // Data state
    const [trainers, setTrainers] = useState([]);
    const [services, setServices] = useState([]);
    const [halls, setHalls] = useState([]);
    const [workingHours, setWorkingHours] = useState([]);
    const [confirmedTrainings, setConfirmedTrainings] = useState([]);
    const [pendingBookings, setPendingBookings] = useState([]);
    const [groupTrainings, setGroupTrainings] = useState([]);
    const [lockedSlots, setLockedSlots] = useState([]);
    const [schoolSettings, setSchoolSettings] = useState(null);

    // Loading and error state
    const [loading, setLoading] = useState({
        trainers: false,
        services: false,
        halls: false,
        workingHours: false,
        events: false,
    });

    const [errors, setErrors] = useState({
        trainers: null,
        services: null,
        halls: null,
        workingHours: null,
        events: null,
    });

    // Fetch trainers
    const fetchTrainers = useCallback(async () => {
        if (!schoolId) return;

        setLoading(prev => ({ ...prev, trainers: true }));
        setErrors(prev => ({ ...prev, trainers: null }));

        try {
            const response = await axios.get(`/api/v1/trainer/find-all/${schoolId}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` },
            });
            setTrainers(response.data || []);
        } catch (error) {
            setErrors(prev => ({ ...prev, trainers: error.message }));
            console.error('Failed to fetch trainers:', error);
        } finally {
            setLoading(prev => ({ ...prev, trainers: false }));
        }
    }, [schoolId]);

    // Fetch services
    const fetchServices = useCallback(async () => {
        if (!schoolId) return;

        setLoading(prev => ({ ...prev, services: true }));
        setErrors(prev => ({ ...prev, services: null }));

        try {
            const response = await axios.get(`/api/v1/services/school/${schoolId}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` },
            });
            setServices(response.data || []);
        } catch (error) {
            setErrors(prev => ({ ...prev, services: error.message }));
            console.error('Failed to fetch services:', error);
        } finally {
            setLoading(prev => ({ ...prev, services: false }));
        }
    }, [schoolId]);

    // Fetch halls
    const fetchHalls = useCallback(async () => {
        if (!schoolId) return;

        setLoading(prev => ({ ...prev, halls: true }));
        setErrors(prev => ({ ...prev, halls: null }));

        try {
            const response = await axios.get(`/api/v1/school/${schoolId}/halls`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` },
            });
            setHalls(response.data || []);
        } catch (error) {
            setErrors(prev => ({ ...prev, halls: error.message }));
            console.error('Failed to fetch halls:', error);
        } finally {
            setLoading(prev => ({ ...prev, halls: false }));
        }
    }, [schoolId]);

    // Fetch working hours
    const fetchWorkingHours = useCallback(async () => {
        if (!trainerId) return;

        setLoading(prev => ({ ...prev, workingHours: true }));
        setErrors(prev => ({ ...prev, workingHours: null }));

        try {
            const response = await axios.get(`/api/v1/trainer/get-working-hours/${trainerId}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` },
            });
            setWorkingHours(response.data || []);
        } catch (error) {
            setErrors(prev => ({ ...prev, workingHours: error.message }));
            console.error('Failed to fetch working hours:', error);
        } finally {
            setLoading(prev => ({ ...prev, workingHours: false }));
        }
    }, [trainerId]);

    // Fetch all calendar events for the week
    const fetchCalendarEvents = useCallback(async () => {
        if (!trainerId || !weekStart) return;

        setLoading(prev => ({ ...prev, events: true }));
        setErrors(prev => ({ ...prev, events: null }));

        const startTime = weekStart.startOf('isoWeek').format('YYYY-MM-DDTHH:mm:ss');
        const endTime = weekStart.endOf('isoWeek').format('YYYY-MM-DDTHH:mm:ss');
        const token = localStorage.getItem('jwt');

        try {
            // Fetch all event types in parallel
            const [pendingRes, confirmedRes, groupRes, lockRes] = await Promise.all([
                axios.get(`/api/v1/training/bookings-for-week/${trainerId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { startTime, endTime },
                }),
                axios.get(`/api/v1/training/getConfirmedTrainingsForTrainer/${trainerId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { startTime, endTime },
                }),
                axios.get(`/api/v1/group-trainings/trainer/${trainerId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { startTime, endTime },
                }),
                axios.get(`/api/v1/training/lock/${trainerId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { startTime, endTime },
                }),
            ]);

            setPendingBookings(pendingRes.data || []);
            setConfirmedTrainings(confirmedRes.data || []);

            // Group trainings are already filtered by the backend with date params
            // Transform to include startTime and endTime for calendar compatibility
            const allGroupTrainings = (groupRes.data || []).map(training => ({
                ...training,
                startTime: training.scheduledAt,
                endTime: dayjs(training.scheduledAt).add(training.durationMinutes || 60, 'minute').toISOString(),
            }));

            setGroupTrainings(allGroupTrainings);
            setLockedSlots(lockRes.data || []);
        } catch (error) {
            setErrors(prev => ({ ...prev, events: error.message }));
            console.error('Failed to fetch calendar events:', error);
        } finally {
            setLoading(prev => ({ ...prev, events: false }));
        }
    }, [trainerId, weekStart]);

    // Fetch school settings
    const fetchSchoolSettings = useCallback(async () => {
        if (!schoolId) return;

        try {
            const response = await axios.get(`/api/v1/school/${schoolId}/settings`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` },
            });
            setSchoolSettings(response.data);
        } catch (error) {
            console.error('Failed to fetch school settings:', error);
        }
    }, [schoolId]);

    // Refresh all calendar data
    const refreshCalendar = useCallback(() => {
        fetchCalendarEvents();
    }, [fetchCalendarEvents]);

    // Book a new training
    const bookTraining = useCallback(async (bookingData) => {
        const token = localStorage.getItem('jwt');

        try {
            const response = await axios.post(
                '/api/v1/training/trainer/book-training',
                bookingData,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            // Refresh calendar after booking
            refreshCalendar();

            return { success: true, data: response.data };
        } catch (error) {
            console.error('Failed to book training:', error);
            return { success: false, error: error.message };
        }
    }, [refreshCalendar]);

    // Update a training
    const updateTraining = useCallback(async (updateData) => {
        const token = localStorage.getItem('jwt');

        try {
            await axios.patch('/api/v1/training/edit', updateData, {
                headers: { Authorization: `Bearer ${token}` },
            });

            // Refresh calendar after update
            refreshCalendar();

            return { success: true };
        } catch (error) {
            console.error('Failed to update training:', error);
            return { success: false, error: error.message };
        }
    }, [refreshCalendar]);

    // Delete a training
    const deleteTraining = useCallback(async (trainingId) => {
        const token = localStorage.getItem('jwt');

        try {
            await axios.post(`/api/v1/training/${trainingId}/decline`, {}, {
                headers: { Authorization: `Bearer ${token}` },
            });

            // Refresh calendar after deletion
            refreshCalendar();

            return { success: true };
        } catch (error) {
            console.error('Failed to delete training:', error);
            return { success: false, error: error.message };
        }
    }, [refreshCalendar]);

    // Lock time slots
    const lockTimeSlots = useCallback(async (lockData) => {
        const token = localStorage.getItem('jwt');

        try {
            const response = await axios.post('/api/v1/training/lock', lockData, {
                headers: { Authorization: `Bearer ${token}` },
            });

            // Refresh calendar after locking
            refreshCalendar();

            return { success: true, data: response.data };
        } catch (error) {
            console.error('Failed to lock time slots:', error);
            return { success: false, error: error.message };
        }
    }, [refreshCalendar]);

    const updateLock = useCallback(async (updateData) => {
        const token = localStorage.getItem('jwt');

        try {
            const response = await axios.put('/api/v1/training/lock', updateData, {
                headers: { Authorization: `Bearer ${token}` },
            });

            // Refresh calendar after update
            refreshCalendar();

            return { success: true, data: response.data };
        } catch (error) {
            console.error('Failed to update lock:', error);
            return { success: false, error: error.message };
        }
    }, [refreshCalendar]);

    // Delete a lock
    const deleteLock = useCallback(async (lockId) => {
        const token = localStorage.getItem('jwt');

        try {
            await axios.delete(`/api/v1/training/lock/${lockId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            // Refresh calendar after deletion
            refreshCalendar();

            return { success: true };
        } catch (error) {
            console.error('Failed to delete lock:', error);
            return { success: false, error: error.message };
        }
    }, [refreshCalendar]);

    // Initial data fetching
    useEffect(() => {
        fetchTrainers();
        fetchServices();
        fetchHalls();
        fetchSchoolSettings();
    }, [fetchTrainers, fetchServices, fetchHalls, fetchSchoolSettings]);

    useEffect(() => {
        fetchWorkingHours();
    }, [fetchWorkingHours]);

    useEffect(() => {
        fetchCalendarEvents();
    }, [fetchCalendarEvents, weekStart]); // Refresh when week changes

    // Get merged calendar events
    const allEvents = mergeCalendarEvents({
        confirmedTrainings,
        groupTrainings,
        lockedSlots,
    });

    // Check if any data is loading
    const isLoading = Object.values(loading).some(val => val);

    // Check if there are any errors
    const hasErrors = Object.values(errors).some(val => val !== null);

    return {
        // Data
        trainers,
        services,
        halls,
        workingHours,
        confirmedTrainings,
        pendingBookings,
        groupTrainings,
        lockedSlots,
        schoolSettings,
        allEvents,

        // Loading and error state
        loading,
        errors,
        isLoading,
        hasErrors,

        // Actions
        refreshCalendar,
        bookTraining,
        updateTraining,
        deleteTraining,
        lockTimeSlots,
        updateLock,
        deleteLock,

        // Fetch functions (for manual refresh)
        fetchTrainers,
        fetchServices,
        fetchHalls,
        fetchWorkingHours,
        fetchCalendarEvents,
        fetchSchoolSettings,
    };
};